import React, { useMemo, useEffect, useRef } from 'react';
import { BackgroundThemeRevision } from 'referral-types';
import { rgbStringToHex } from '../../../utils/colorUtils';

export const Matrix: React.FC<{ themeRevision?: Partial<BackgroundThemeRevision> }> = (props) => {
  const { themeRevision } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const subTheme = themeRevision?.subTheme;

  const fillStyle = useMemo(() => {
    switch (subTheme) {
      case 'default':
        return '#00ff41';
      case 'primary':
        return rgbStringToHex(document.body.style.getPropertyValue('--primary-500')) || '0 255 65';
      case 'secondary':
        return rgbStringToHex(document.body.style.getPropertyValue('--secondary-500')) || '0 255 65';
      case 'tertiary':
        return rgbStringToHex(document.body.style.getPropertyValue('--tertiary-500')) || '0 255 65';
      default:
        return '#00ff41';
    }
  }, [subTheme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Matrix characters - alphanumeric and symbols
    const matrix = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ123456789@#$%^&*()*&^%+-/~{[|`]}';
    const matrixArray = matrix.split('');

    const fontSize = 16;
    let columns: number;
    let drops: number[] = [];
    let animationId: number;
    let lastFrameTime = 0;
    const targetFPS = 30;
    const frameInterval = 1000 / targetFPS;

    const resizeCanvas = () => {
      canvas.height = window.innerHeight;
      canvas.width = window.innerWidth;
      columns = canvas.width / fontSize;

      // Reset drops array when canvas is resized with random starting positions
      drops = [];
      for (let x = 0; x < columns; x++) {
        // Start each drop at a random position on the screen
        drops[x] = Math.floor(Math.random() * (canvas.height / fontSize));
      }
    };

    const draw = (timestamp: number) => {
      // Throttle to target FPS
      const elapsed = timestamp - lastFrameTime;

      if (elapsed < frameInterval) {
        animationId = requestAnimationFrame(draw);
        return;
      }

      lastFrameTime = timestamp - (elapsed % frameInterval);

      // Black background with slight transparency for trail effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.04)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Green text color (iconic matrix)
      ctx.fillStyle = fillStyle;
      ctx.font = `${fontSize}px arial`;

      // Loop over drops
      for (let i = 0; i < drops.length; i++) {
        // Random character to print
        const text = matrixArray[Math.floor(Math.random() * matrixArray.length)];

        // Draw the character
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);

        // Reset drop to top randomly after it crosses the screen
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }

        // Increment Y coordinate (slower)
        if (Math.random() > 0.5) {
          drops[i]++;
        }
      }

      animationId = requestAnimationFrame(draw);
    };

    // Initialize canvas
    resizeCanvas();

    // Start animation using requestAnimationFrame instead of setInterval
    animationId = requestAnimationFrame(draw);

    // Handle window resize
    const handleResize = () => {
      resizeCanvas();
    };

    // Pause animation when tab is not visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      } else {
        animationId = requestAnimationFrame(draw);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // Cleanup
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fillStyle]);

  return (
    <div className="-z-10 h-full w-full">
      <canvas ref={canvasRef} className="fixed inset-0 h-full w-full bg-black" />
    </div>
  );
};
